import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { Button } from '@/components/ui/button';
import { TopVendorData, TopVendorMetric } from '@/hooks/useVendorPerformance';
import { Loader2, Award } from 'lucide-react';
import { cn } from '@/lib/utils';

interface TopPerformingVendorsChartProps {
  data: TopVendorData[] | undefined;
  isLoading: boolean;
  metric: TopVendorMetric;
  onMetricChange: (metric: TopVendorMetric) => void;
}

const COLORS = [
  'hsl(var(--chart-1))',
  'hsl(var(--chart-2))',
  'hsl(var(--chart-3))',
  'hsl(var(--chart-4))',
  'hsl(var(--chart-5))',
  'hsl(var(--chart-1))',
  'hsl(var(--chart-2))',
  'hsl(var(--chart-3))',
  'hsl(var(--chart-4))',
  'hsl(var(--chart-5))',
];

function formatValue(value: number, metric: TopVendorMetric): string {
  switch (metric) {
    case 'value':
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(value);
    case 'winRate':
      return `${value.toFixed(0)}%`;
    case 'responseTime':
      if (value < 48) {
        return `${Math.round(value)}h`;
      }
      return `${(value / 24).toFixed(1)}d`;
  }
}

function getMetricLabel(metric: TopVendorMetric): string {
  switch (metric) {
    case 'value':
      return 'Total Value';
    case 'winRate':
      return 'Win Rate';
    case 'responseTime':
      return 'Response Time';
  }
}

function truncateName(name: string, maxLength: number = 18): string {
  return name.length > maxLength ? `${name.substring(0, maxLength)}...` : name;
}

export function TopPerformingVendorsChart({
  data,
  isLoading,
  metric,
  onMetricChange,
}: TopPerformingVendorsChartProps) {
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Top Performing Vendors</CardTitle>
          <CardDescription>Best vendors by selected metric</CardDescription>
        </CardHeader>
        <CardContent className="h-[300px] flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Top Performing Vendors</CardTitle>
            <CardDescription>Best vendors by selected metric</CardDescription>
          </div>
          <MetricToggle metric={metric} onMetricChange={onMetricChange} />
        </CardHeader>
        <CardContent className="h-[300px] flex items-center justify-center">
          <p className="text-muted-foreground">No vendor data for this period</p>
        </CardContent>
      </Card>
    );
  }

  // Prepare chart data with truncated names
  const chartData = data.map(vendor => ({
    ...vendor,
    displayName: truncateName(vendor.vendorName),
    hasCerts: vendor.certifications.length > 0,
  }));

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>Top Performing Vendors</CardTitle>
          <CardDescription>Top 10 vendors by {getMetricLabel(metric).toLowerCase()}</CardDescription>
        </div>
        <MetricToggle metric={metric} onMetricChange={onMetricChange} />
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart
            data={chartData}
            layout="vertical"
            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
            <XAxis
              type="number"
              tickFormatter={(value) => formatValue(value, metric)}
              tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
            />
            <YAxis
              type="category"
              dataKey="displayName"
              width={130}
              tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: 'hsl(var(--popover))',
                border: '1px solid hsl(var(--border))',
                borderRadius: '8px',
              }}
              formatter={(value: number) => [formatValue(value, metric), getMetricLabel(metric)]}
              labelFormatter={(label, payload) => {
                const vendor = payload?.[0]?.payload;
                if (!vendor) return label;
                const certInfo = vendor.hasCerts ? ' 🏆' : '';
                return `${vendor.vendorName}${certInfo}`;
              }}
            />
            <Bar dataKey="value" radius={[0, 4, 4, 0]}>
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % 10]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
        
        {/* Legend for diversity certification */}
        {chartData.some(v => v.hasCerts) && (
          <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
            <Award className="h-3 w-3" />
            <span>Vendors with 🏆 have diversity certifications</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function MetricToggle({
  metric,
  onMetricChange,
}: {
  metric: TopVendorMetric;
  onMetricChange: (metric: TopVendorMetric) => void;
}) {
  return (
    <div className="flex gap-1 p-1 bg-muted rounded-lg">
      <Button
        variant="ghost"
        size="sm"
        className={cn(
          'h-7 px-2 text-xs',
          metric === 'value' && 'bg-background shadow-sm'
        )}
        onClick={() => onMetricChange('value')}
      >
        Value
      </Button>
      <Button
        variant="ghost"
        size="sm"
        className={cn(
          'h-7 px-2 text-xs',
          metric === 'winRate' && 'bg-background shadow-sm'
        )}
        onClick={() => onMetricChange('winRate')}
      >
        Win Rate
      </Button>
      <Button
        variant="ghost"
        size="sm"
        className={cn(
          'h-7 px-2 text-xs',
          metric === 'responseTime' && 'bg-background shadow-sm'
        )}
        onClick={() => onMetricChange('responseTime')}
      >
        Response
      </Button>
    </div>
  );
}
